import { Link, createFileRoute } from '@tanstack/react-router'
import { keepPreviousData } from '@tanstack/react-query'

import { useGetMonthlyReport } from '../../../api/generated/endpoints/admin/admin'
import type { MonthlyReportRead } from '../../../api/generated/model'
import { formatHours } from '../../../lib/credits'
import { addMonths, monthLabel } from '../../../lib/month'
import { formatCount, formatPercent } from '../../../lib/figures'
import { formatUYU } from '../../../lib/money'
import { todayAtFacility } from '../../../lib/time'

/**
 * The owner's screen, and the only one here that is not about running a day.
 *
 * ## Money and hours are side by side, never added
 *
 * An hour bought in March and played in June is money in March and bay time in
 * June. The API returns them as separate sections for that reason and this
 * screen keeps them visually separate, with the overlap named out loud ("de
 * ellas, N con horas compradas antes") so nobody reads the two totals as parts
 * of one figure.
 *
 * ## The denominator is always on screen
 *
 * Occupancy divides by hours the facility was actually open and unblocked --
 * otherwise a fortnight shut reads as failure rather than as closed. But that
 * also means blocking everything would show a high percentage of almost
 * nothing, so the hours it divided by are printed underneath it. The number is
 * never presented without what it was measured against.
 */
export const Route = createFileRoute('/_authed/_admin/admin/reports')({
  validateSearch: (search: Record<string, unknown>): { month?: string } => ({
    month: isMonth(search.month) ? search.month : undefined,
  }),
  component: ReportsPage,
})

const isMonth = (value: unknown): value is string =>
  typeof value === 'string' && /^\d{4}-(0[1-9]|1[0-2])$/.test(value)

function ReportsPage() {
  const { month } = Route.useSearch()
  const thisMonth = todayAtFacility().slice(0, 7)
  const shown = month ?? thisMonth

  const report = useGetMonthlyReport(
    { month: shown },
    {
      query: {
        staleTime: 60_000,
        // Keeps the last month on screen while the next one loads, rather than
        // collapsing to a skeleton between taps.
        placeholderData: keepPreviousData,
      },
    },
  )

  return (
    <div className="space-y-8">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <Link
          to="/admin/reports"
          search={{ month: addMonths(shown, -1) }}
          className="inline-flex min-h-16 items-center rounded-md border-2 border-slate-400 bg-white px-6 text-lg focus-visible:outline-4 focus-visible:outline-offset-2 focus-visible:outline-slate-900"
        >
          &lsaquo;&nbsp; Mes anterior
        </Link>

        {/* first-letter, NOT `capitalize` -- that is per-word and turned
            'julio de 2026' into 'Julio De 2026'. */}
        <h1 className="text-3xl font-semibold first-letter:uppercase">{monthLabel(shown)}</h1>

        {/* No point offering a month that cannot have happened yet. */}
        {shown < thisMonth ? (
          <Link
            to="/admin/reports"
            search={{ month: addMonths(shown, 1) }}
            className="inline-flex min-h-16 items-center rounded-md border-2 border-slate-400 bg-white px-6 text-lg focus-visible:outline-4 focus-visible:outline-offset-2 focus-visible:outline-slate-900"
          >
            Mes siguiente &nbsp;&rsaquo;
          </Link>
        ) : (
          <span className="inline-flex min-h-16 items-center px-6 text-lg text-slate-500">
            No hay meses posteriores
          </span>
        )}
      </div>

      {report.isPending && <p className="text-xl">Cargando los números…</p>}

      {report.isError && (
        <div role="alert" className="rounded-lg border-2 border-red-300 bg-red-50 p-6">
          <p className="text-xl text-red-900">No se pudieron cargar los números.</p>
          <button
            type="button"
            onClick={() => void report.refetch()}
            className="mt-4 min-h-16 rounded-md bg-slate-900 px-8 text-xl text-white focus-visible:outline-4 focus-visible:outline-offset-2 focus-visible:outline-slate-900"
          >
            Reintentar
          </button>
        </div>
      )}

      {report.data && <Figures report={report.data} />}
    </div>
  )
}

function Figures({ report }: { report: MonthlyReportRead }) {
  const { revenue, hours, utilization: use, sessions } = report

  // A month with nothing in it is otherwise a wall of zeros that reads like a
  // loading failure. Say the quiet part instead.
  const empty = sessions.total === 0 && sessions.cancelled === 0 && use.blocked_hours === 0

  return (
    <>
      {empty && (
        <p className="rounded-lg border-2 border-dashed border-slate-300 bg-white p-5 text-lg text-slate-600">
          No hubo movimiento en este mes: ninguna reserva, ningún bloqueo.
        </p>
      )}

      {report.partial && (
        <p role="status" className="rounded-lg border-2 border-amber-400 bg-amber-50 p-5 text-lg">
          El mes todavía está en curso, así que las cifras van a seguir subiendo.
        </p>
      )}

      <div className="grid gap-6 lg:grid-cols-2">
        {/* The hint used to say "lo cobrado", meaning CHARGED -- which stopped
            being safe wording the moment a second figure on this card started
            meaning collected. Those were the same number while Mercado Pago
            took the money up front, and are not now. */}
        <Card
          title="Ingresos"
          hint="Lo que le corresponde al local de lo facturado este mes, al precio de ese momento. De una clase, el local se queda con lo que paga el profesor por el simulador."
        >
          <Figure label="Sesiones" value={formatUYU(revenue.sessions_uyu_pesos)} />
          <Figure
            label="De eso, ya cobrado"
            value={formatUYU(revenue.sessions_collected_uyu_pesos)}
          />
          {revenue.sessions_uyu_pesos > revenue.sessions_collected_uyu_pesos && (
            // The gap is the only number on this screen he can act on, so it is
            // stated rather than left to be subtracted -- and it says where to
            // go and do something about it.
            <p className="text-base text-amber-900">
              Quedan {formatUYU(revenue.sessions_uyu_pesos - revenue.sessions_collected_uyu_pesos)}{' '}
              sin cobrar. Están en Pagos.
            </p>
          )}
          <Figure label="Paquetes de horas" value={formatUYU(revenue.packages_uyu_pesos)} />
          <Figure label="Torneos" value={formatUYU(revenue.tournaments_uyu_pesos)} />
          <Figure label="Total" value={formatUYU(revenue.total_uyu_pesos)} strong />
          {/* Beside the total, never inside it: it is the coaches' money, and
              a figure that mixed the two would overstate the facility by
              every class taught. Stated so it is never read as missing. Not
              "cobrado": on this card that word means handed over, and this is
              what the classes were charged at, paid or not. */}
          {revenue.coaches_uyu_pesos > 0 && (
            <>
              <Figure
                label="Clases, para los profesores"
                value={formatUYU(revenue.coaches_uyu_pesos)}
              />
              <p className="text-base text-slate-600">
                Es de ellos, por sus clases: no está en el total del local.
              </p>
            </>
          )}
          {revenue.packages_uyu_pesos === 0 && (
            <p className="text-base text-slate-600">
              Los paquetes recién aparecen acá cuando los pagos estén habilitados.
            </p>
          )}
        </Card>

        <Card
          title="Horas de simulador"
          hint="Tiempo de cancha usado. No se suma a los ingresos: una hora comprada antes ya se cobró en su momento."
        >
          <Figure label="Horas jugadas" value={formatHours(hours.played)} strong />
          <Figure
            label="De ellas, con horas compradas antes"
            value={formatHours(hours.on_credit)}
          />
          <Figure label="Horas vendidas en paquetes" value={formatHours(hours.sold_in_packages)} />
        </Card>

        <Card
          title="Ocupación"
          hint="Sobre las horas que el local estuvo realmente abierto y sin bloquear."
        >
          <Figure label="Ocupación" value={formatPercent(use.percent)} strong />
          <Figure
            label="Horas ocupadas"
            value={`${formatCount(use.booked_hours)} de ${formatHours(use.open_hours)}`}
          />
          {/* Printed so the percentage can never be read without what it
              divided by. Blocking the whole month would show a high number
              against almost nothing, and these lines say so. */}
          <Figure label="Horas bloqueadas" value={formatHours(use.blocked_hours)} />
          <Figure
            label="Días cerrados"
            value={use.closed_days === 1 ? '1 día' : `${use.closed_days} días`}
          />
          <Figure
            label="Máximo posible con todo abierto"
            value={`${formatHours(use.possible_hours)} en ${
              use.bays === 1 ? '1 simulador' : `${use.bays} simuladores`
            }`}
          />
        </Card>

        <Card title="Sesiones" hint="Las que llegaron a su hora, sin contar las canceladas antes.">
          <Figure label="Sesiones" value={formatCount(sessions.total)} strong />
          <Figure label="Práctica" value={formatCount(sessions.practice)} />
          <Figure label="Clases" value={formatCount(sessions.classes)} />
          <Figure label="Torneo" value={formatCount(sessions.tournament)} />
          <Figure
            label="No se presentaron"
            value={`${formatCount(sessions.no_shows)} (${formatPercent(sessions.no_show_percent)})`}
          />
          <Figure label="Canceladas antes de la hora" value={formatCount(sessions.cancelled)} />
        </Card>

        {report.coaches.length > 0 && (
          <Card
            title="Clases por profesor"
            hint="De mayor a menor. Lo cobrado, y de eso cuánto es del profesor y cuánto del local."
          >
            {report.coaches.map((coach) => (
              <div key={coach.name}>
                <Figure
                  label={coach.name}
                  value={`${formatCount(coach.classes)} · ${formatUYU(coach.charged_uyu_pesos)}`}
                />
                {coach.charged_uyu_pesos > 0 && (
                  <p className="text-base text-slate-600">
                    {formatUYU(coach.coach_uyu_pesos)} del profesor ·{' '}
                    {formatUYU(coach.facility_uyu_pesos)} del local
                  </p>
                )}
              </div>
            ))}
          </Card>
        )}

        {report.rates.length > 0 && (
          <Card
            title="Por tarifa"
            hint="Cuántas veces se vendió cada tarifa y lo facturado. Las reservas de antes de las tarifas no figuran."
          >
            {report.rates.map((rate) => (
              <Figure
                key={rate.name}
                label={rate.name}
                value={`${formatCount(rate.sessions)} · ${formatUYU(rate.charged_uyu_pesos)}`}
              />
            ))}
          </Card>
        )}
      </div>
    </>
  )
}

function Card({
  title,
  hint,
  children,
}: {
  title: string
  hint: string
  children: React.ReactNode
}) {
  return (
    <section className="rounded-lg border-2 border-slate-300 bg-white p-6">
      <h2 className="text-2xl font-semibold">{title}</h2>
      <p className="mt-1 text-base text-slate-600">{hint}</p>
      <dl className="mt-5 space-y-3">{children}</dl>
    </section>
  )
}

function Figure({ label, value, strong }: { label: string; value: string; strong?: boolean }) {
  return (
    <div className="flex flex-wrap items-baseline justify-between gap-x-4 border-b border-slate-100 pb-2 last:border-b-0">
      <dt className={strong ? 'text-xl font-medium' : 'text-lg text-slate-700'}>{label}</dt>
      <dd
        className={[
          'tabular-nums',
          strong ? 'text-2xl font-semibold' : 'text-xl text-slate-900',
        ].join(' ')}
      >
        {value}
      </dd>
    </div>
  )
}
